import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";

/**
 * Features:
 * ✅ Nur Bilder (kein Title/Description)
 * ✅ Autoplay
 * ✅ Pause on hover (Desktop) + Pause während Drag/Swipe
 * ✅ Swipe (Touch + Mouse/Pointer)
 * ✅ Preload aller Bilder (weniger “Flackern”)
 * ✅ Dots + Keyboard (←/→)
 */
export default function ImageSlider({
                                        images,
                                        interval = 5000,
                                        styles,
                                        pauseOnHover = true,
                                        enableSwipe = true,
                                        swipeThreshold = 50,
                                        transitionDuration = 1,
                                    }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    const timerRef = useRef(null);
    const startXRef = useRef(null);
    const draggingRef = useRef(false);

    const len = images?.length || 0;

    const clampIndex = (idx) => {
        if (!len) return 0;
        return (idx + len) % len;
    };

    const goTo = (idx) => setCurrentIndex(clampIndex(idx));
    const next = () => setCurrentIndex((i) => clampIndex(i + 1));
    const prev = () => setCurrentIndex((i) => clampIndex(i - 1));

    // Preload images
    useEffect(() => {
        if (!len) return;
        images.forEach((img) => {
            if (!img?.url) return;
            const pre = new Image();
            pre.src = img.url;
        });
    }, [images, len]);

    // Autoplay (pausierbar)
    useEffect(() => {
        if (!len || len === 1) return;

        if (timerRef.current) clearInterval(timerRef.current);

        if (!isPaused) {
            timerRef.current = setInterval(() => {
                setCurrentIndex((i) => clampIndex(i + 1));
            }, interval);
        }

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interval, isPaused, len]);

    // Reset index if images length changed
    useEffect(() => {
        if (!len) return;
        setCurrentIndex((i) => clampIndex(i));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [len]);

    // Swipe / Drag handlers (Pointer Events)
    const onPointerDown = (e) => {
        if (!enableSwipe || !len || len === 1) return;
        draggingRef.current = true;
        startXRef.current = e.clientX ?? (e.touches?.[0]?.clientX ?? null);
        setIsPaused(true);
    };

    const onPointerUp = (e) => {
        if (!enableSwipe || !draggingRef.current) return;

        const endX = e.clientX ?? (e.changedTouches?.[0]?.clientX ?? null);
        const startX = startXRef.current;

        draggingRef.current = false;
        startXRef.current = null;

        if (startX == null || endX == null) {
            setIsPaused(false);
            return;
        }

        const delta = endX - startX;

        if (Math.abs(delta) >= swipeThreshold) {
            if (delta > 0) prev();
            else next();
        }

        setIsPaused(false);
    };

    const onPointerCancel = () => {
        draggingRef.current = false;
        startXRef.current = null;
        setIsPaused(false);
    };

    const onKeyDown = (e) => {
        if (!len || len === 1) return;
        if (e.key === "ArrowLeft") prev();
        if (e.key === "ArrowRight") next();
    };

    return (
        <div
            style={styles.slider}
            tabIndex={0}
            onKeyDown={onKeyDown}
            onMouseEnter={pauseOnHover ? () => setIsPaused(true) : undefined}
            onMouseLeave={pauseOnHover ? () => setIsPaused(false) : undefined}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            // Touch fallback (manche Browser feuern Pointer nicht sauber)
            onTouchStart={(e) => onPointerDown(e)}
            onTouchEnd={(e) => onPointerUp(e)}
            onTouchCancel={onPointerCancel}
            aria-label="Bilder-Slider"
        >
            {images?.map((image, index) => (
                <motion.div
                    key={index}
                    style={{
                        ...styles.slide,
                        backgroundImage: `url(${image.url})`,
                        opacity: index === currentIndex ? 1 : 0,
                        zIndex: index === currentIndex ? 1 : 0,
                        // optional: leichter “Ken Burns”-Look nur fürs aktive Bild
                        transform: index === currentIndex ? "scale(1.02)" : "scale(1)",
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: index === currentIndex ? 1 : 0 }}
                    transition={{ duration: transitionDuration }}
                />
            ))}

            {len > 1 && (
                <div style={styles.sliderDots}>
                    {images.map((_, index) => (
                        <motion.button
                            key={index}
                            style={{
                                ...styles.sliderDot,
                                backgroundColor: index === currentIndex ? "#8D6E63" : "#ffffff",
                            }}
                            onClick={() => goTo(index)}
                            whileHover={{ scale: 1.2 }}
                            whileTap={{ scale: 0.95 }}
                            aria-label={`Slide ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
